import { useEffect, useState } from "react";
import { ShieldCheck, DownloadSimple, X } from "@phosphor-icons/react";

// Compact "branded" top strip used on pages that don't show the full <Header />,
// e.g. the Voter Login. Also surfaces a one-tap "Install app" CTA on supported
// browsers (Chrome / Edge / Brave) via the beforeinstallprompt event.
export default function MobileBrandBar({ subtitle = "Verified Voter Ledger" }) {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        try { return sessionStorage.getItem("pwa-install-dismissed") === "1"; }
        catch { return false; }
    });

    useEffect(() => {
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferredPrompt(null);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);
        // Detect if already running as installed PWA
        if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
            setInstalled(true);
        }
        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const install = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        try { await deferredPrompt.userChoice; } catch (_) {}
        setDeferredPrompt(null);
    };

    const dismiss = () => {
        setDismissed(true);
        try { sessionStorage.setItem("pwa-install-dismissed", "1"); } catch (_) {}
    };

    const showInstall = !!deferredPrompt && !installed && !dismissed;

    return (
        <div
            className="lg:hidden sticky top-0 z-40 bg-civic-900 text-white border-b border-civic-700/50"
            data-testid="mobile-brand-bar"
        >
            <div className="px-4 h-12 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="relative shrink-0">
                        <ShieldCheck size={20} weight="duotone" className="text-civic-200" />
                        <span className="absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-emerald-400 ring-2 ring-civic-900" />
                    </div>
                    <div className="leading-tight min-w-0">
                        <div className="font-heading font-black text-sm tracking-tight whitespace-nowrap" data-testid="mobile-brand-title">
                            E-VoteVerify<span className="text-civic-200">.com</span>
                        </div>
                        <div className="text-[9px] uppercase tracking-widest text-civic-200/80 leading-none truncate">{subtitle}</div>
                    </div>
                </div>
                {showInstall && (
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            type="button"
                            onClick={install}
                            className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-civic-900 font-semibold text-[11px] px-2.5 py-1 rounded transition-colors"
                            data-testid="pwa-install-btn"
                        >
                            <DownloadSimple size={13} weight="bold" /> Install
                        </button>
                        <button
                            type="button"
                            onClick={dismiss}
                            aria-label="Dismiss install prompt"
                            className="p-1 rounded text-civic-200/70 hover:text-white hover:bg-white/10 transition-colors"
                            data-testid="pwa-install-dismiss"
                        >
                            <X size={12} weight="bold" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
